/* import __COLOCATED_TEMPLATE__ from './modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ConversationExtractionService from 'embercom/services/conversation-extraction-service';

import { type IntegrationWarningReason } from './integration-warning';

interface Args {
  modal: any;
  title?: string;
  onNavigationNext?: (viewId?: string) => void;
  onNavigationBack?: () => void;
  onModalClose?: () => void;
  showBackButton?: boolean;
}

export default class Modal extends Component<Args> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service intercomEventService: $TSFixMe;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare conversationExtractionService: ConversationExtractionService;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.ensureSettingsLoaded();
  }

  private async ensureSettingsLoaded() {
    if (!this.conversationExtractionService.conversationExtractionSettings) {
      await this.conversationExtractionService.fetchConversationExtractionSettings();
    }
  }

  get integrationWarning(): IntegrationWarningReason | null {
    return (
      this.appService.mainStandalonePlatform.componentData['knowledge-hub-modal']
        ?.integrationWarning ?? null
    );
  }

  get isSettingsSubmitBlocked() {
    return (
      !this.conversationExtractionSettings ||
      this.conversationExtractionSettings.isLatestImportInProgress ||
      !!this.integrationWarning
    );
  }

  get settingsBlockedReason() {
    if (this.isLatestImportInProgress) {
      return this.intl.t(
        'knowledge-hub.conversational.conversation-extraction-settings.settings-blocked-reason.import-in-progress',
      );
    }

    return (
      this.appService.mainStandalonePlatform.componentData['knowledge-hub-modal']
        ?.settingsBlockedReason ?? ''
    );
  }

  private get isLatestImportInProgress() {
    return this.conversationExtractionSettings?.isLatestImportInProgress;
  }

  private get conversationExtractionSettings() {
    return this.conversationExtractionService.conversationExtractionSettings;
  }

  @action async submitSettings() {
    if (!this.conversationExtractionSettings || this.isSettingsSubmitBlocked) {
      return;
    }

    await this.knowledgeHubService.ensurePermissions();

    try {
      this.conversationExtractionSettings.enabled = true;
      await this.conversationExtractionSettings.save();
      this.intercomEventService.trackAnalyticsEvent({
        action: 'saved',
        object: 'conversation_extraction_settings',
        enabled: true,
        admin_ids: [],
        team_ids: [],
        default_chatbot_availability:
          this.conversationExtractionSettings?.defaultChatbotAvailability,
        default_copilot_availability:
          this.conversationExtractionSettings?.defaultCopilotAvailability,
      });
      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'knowledge-hub.conversational.conversation-extraction-settings.update-notifications.success',
        ),
      );
      if (this.args.onModalClose) {
        this.args.onModalClose();
      }
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t(
          'knowledge-hub.conversational.conversation-extraction-settings.update-notifications.failure',
        ),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::Conversational::Standalone::Modal': typeof Modal;
  }
}
